import {
  Anchor,
  Badge,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Table,
} from "@mantine/core";
import { DateField, List, ShowButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { TeaAddon } from "entities/tea_addons";
import { useMemo } from "react";
import { Link } from "react-router-dom";

export const TeaAddonList: React.FC = () => {
  const columns = useMemo<ColumnDef<TeaAddon>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
      },
      {
        id: "tea_addon_subscription",
        header: "Tea Addon Subscription",
        accessorKey: "tea_addon_subscription",
        cell: function render({ getValue }) {
          const teaAddonSubscription =
            getValue<TeaAddon["tea_addon_subscription"]>();
          if (!teaAddonSubscription) return <div />;

          return (
            <Anchor
              component={Link}
              to={`/tea_addon_subscriptions/show/${teaAddonSubscription.id}`}
            >
              {teaAddonSubscription.id}
            </Anchor>
          );
        },
        meta: {
          disableSort: true,
        },
      },
      {
        id: "box_name",
        header: "Box Name",
        accessorKey: "box_name",
        cell: function render({ getValue }) {
          const boxName = getValue<TeaAddon["box_name"]>();

          return <Badge color="indigo">{boxName}</Badge>;
        },
        meta: {
          disableSort: true,
        },
      },
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
      },
      {
        id: "user",
        header: "User",
        accessorKey: "user",
        cell: function render({ getValue }) {
          const user = getValue<TeaAddon["user"]>();

          return (
            <Anchor component={Link} to={`/users/show/${user?.id}`}>
              {user?.email}
            </Anchor>
          );
        },
        meta: {
          disableSort: true,
        },
      },
      {
        id: "invoice",
        header: "Invoice",
        accessorKey: "invoice",
        cell: function render({ getValue }) {
          const invoice = getValue<TeaAddon["invoice"]>();
          if (!invoice) return <div />;

          return (
            <Anchor component={Link} to={`/invoices/show/${invoice.id}`}>
              {invoice.remote_id}
            </Anchor>
          );
        },
        meta: {
          disableSort: true,
        },
      },
      {
        id: "order_invoice",
        header: "Order Invoice",
        accessorKey: "invoice",
        cell: function render({ getValue }) {
          const invoice = getValue<TeaAddon["invoice"]>();
          if (!invoice) return <div />;

          return (
            <Anchor component={Link} to={`/invoices/show/${invoice.id}`}>
              {invoice.remote_id}
            </Anchor>
          );
        },
        meta: {
          disableSort: true,
        },
      },
      {
        id: "created_at",
        header: "Created At",
        accessorKey: "created_at",
        cell: function render({ getValue }) {
          const startedAt = getValue<TeaAddon["created_at"]>();

          return <DateField value={startedAt} format="DD/MM/YYYY HH:mm:ss" />;
        },
      },
      {
        id: "last_payment_at",
        header: "Last Payment At",
        accessorKey: "last_payment_at",
        cell: function render({ getValue }) {
          const lastPaymentAt = getValue<TeaAddon["created_at"]>();

          return (
            <DateField value={lastPaymentAt} format="DD/MM/YYYY HH:mm:ss" />
          );
        },
      },
      {
        id: "chargebee_addon_id",
        header: "ChargeBee Addon ID",
        accessorKey: "chargebee_addon_id",
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "id",
        cell: function render({ getValue }) {
          return (
            <Group spacing="xs" noWrap>
              <ShowButton hideText recordItemId={getValue() as number} />
            </Group>
          );
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 50,
      },
    },
  });

  return (
    <List>
      <AppliedFilters<TeaAddon> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<TeaAddon> headerGroups={getHeaderGroups()} />
          <Rows<TeaAddon> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
